import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { escrowAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { BreadcrumbsInline } from '../components/Breadcrumbs';
import { ClientEscrowsSection } from '../components/escrow/ClientEscrowsSection';
import {
  ShieldCheck,
  CurrencyDollar,
  Clock,
  Warning,
  CheckCircle,
  ArrowLeft,
  SpinnerGap,
  Funnel,
  ArrowRight,
  User,
  Briefcase
} from '@phosphor-icons/react';

const EscrowDashboard = () => {
  const navigate = useNavigate();
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_held: 0, total_released: 0, active_escrows: 0 });
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchEscrows();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [filter, page]);

  const fetchEscrows = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 10 };
      if (filter !== 'all') {
        params.status = filter;
      }
      const response = await escrowAPI.getAll(params);
      if (response.data.success) {
        setEscrows(response.data.escrows);
        setTotalPages(response.data.pages);
        setSummary(response.data.summary);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load escrows",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-400',
      funded: 'bg-[#2B4C6F]/10 text-[#2B4C6F] border-[#2B4C6F]',
      partially_released: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-400',
      released: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-400',
      disputed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-400',
      refunded: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-400 border-neutral-400 dark:border-neutral-600'
    };
    return colors[status] || 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-400 border-neutral-400 dark:border-neutral-600';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock size={16} weight="bold" />;
      case 'funded': return <CurrencyDollar size={16} weight="bold" />;
      case 'released': return <CheckCircle size={16} weight="fill" />;
      case 'disputed': return <Warning size={16} weight="fill" />;
      default: return <ShieldCheck size={16} weight="bold" />;
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'funded', label: 'Funded' },
    { value: 'partially_released', label: 'Partial' },
    { value: 'released', label: 'Released' },
    { value: 'disputed', label: 'Disputed' }
  ];

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="escrow-dashboard">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-[#2B4C6F]">
        <div className="max-w-5xl mx-auto px-4">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/contractor-dashboard' },
              { label: 'Escrow' }
            ]}
            className="mb-3"
          />
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
              <ShieldCheck size={36} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black">Escrow Payments</h1>
              <p className="text-blue-100 text-lg mt-1">Secure payments for your projects</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CurrencyDollar size={32} weight="bold" className="text-[#2B4C6F] mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Held in Escrow</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">${summary.total_held?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CheckCircle size={32} weight="fill" className="text-emerald-600 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Released</p>
            <p className="text-2xl font-black text-emerald-600">${summary.total_released?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Briefcase size={32} weight="bold" className="text-purple-600 dark:text-purple-400 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Active</p>
            <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{summary.active_escrows || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-3 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <div className="flex flex-wrap items-center gap-2">
            <Funnel size={20} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-4 py-2 font-bold rounded-lg transition-all ${
                  filter === opt.value 
                    ? 'bg-[#2B4C6F] text-white' 
                    : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                }`}
                data-testid={`filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Escrow List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : escrows.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] text-center">
            <ShieldCheck size={64} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2">No Escrows Found</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">
              {filter !== 'all' ? 'Try changing the filter' : 'Create an escrow when hiring a worker'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {escrows.map((escrow) => (
              <div 
                key={escrow._id}
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:shadow-[6px_6px_0px_#000] dark:hover:shadow-[6px_6px_0px_#2B4C6F] transition-all cursor-pointer"
                onClick={() => navigate(`/escrow/${escrow._id}`)}
                data-testid={`escrow-card-${escrow._id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-black text-neutral-900 dark:text-white">{escrow.job_title || 'Escrow Payment'}</h3>
                      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold border-2 ${getStatusColor(escrow.status)}`}>
                        {getStatusIcon(escrow.status)}
                        {escrow.status?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-neutral-600 dark:text-neutral-400 mb-3">
                      <div className="flex items-center gap-1">
                        <User size={18} weight="bold" />
                        <span className="font-medium">{escrow.worker_name || 'Worker'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={18} weight="bold" />
                        <span className="font-medium">
                          {escrow.created_at ? formatDistanceToNow(new Date(escrow.created_at), { addSuffix: true }) : 'Recently'}
                        </span>
                      </div>
                    </div>

                    {escrow.milestones && escrow.milestones.length > 0 && (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {escrow.milestones.filter(m => m.status === 'released').length} of {escrow.milestones.length} milestones completed
                      </p>
                    )}
                  </div>

                  <div className="text-right ml-4">
                    <p className="text-2xl font-black text-neutral-900 dark:text-white">${escrow.total_amount?.toLocaleString()}</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Amount</p>
                  </div>
                </div>

                <div className="flex justify-end mt-4 pt-4 border-t-2 border-neutral-100 dark:border-neutral-700">
                  <button className="flex items-center gap-2 text-[#2B4C6F] font-bold hover:underline">
                    View Details <ArrowRight size={20} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Client Escrows (homeowner ⇄ contractor) */}
        {localStorage.getItem('userType') === 'contractor' && <ClientEscrowsSection role="contractor" />}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-12 px-6 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold rounded-xl border-2 border-black dark:border-neutral-700 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] disabled:opacity-50 disabled:shadow-none active:translate-y-1 active:shadow-none transition-all"
            >
              Previous
            </button>
            <span className="text-neutral-700 dark:text-neutral-300 font-bold">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="h-12 px-6 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold rounded-xl border-2 border-black dark:border-neutral-700 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] disabled:opacity-50 disabled:shadow-none active:translate-y-1 active:shadow-none transition-all"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EscrowDashboard;
